<script setup>
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useContentStore } from '../stores/contentStore';
import { useRoute, useRouter } from 'vue-router';
import Icon from '../components/Common/Icon.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { gsap } from 'gsap';
import Quote from '../components/Result/Quote.vue';
import Table from '../components/Result/Table.vue';
import { useLocalizationStore } from '../stores/localizationStore';

const route = useRoute();
const contentStore = useContentStore();
const content = ref(contentStore.content.categories[route.params.categoryId].stories[route.params.storyId].result);

const { $trans, $localize } = useLocalizationStore();

const swiper = ref(null);

const sliderContainer = useTemplateRef('slider-container');
onMounted(() => {
    gsap.from(sliderContainer.value, {
        opacity: 0,
        y: 100,
        duration: 0.6,
        ease: "power2.out",
    })
})

const nextStoryId = computed(() => {
    const currentId = route.params.storyId;
    const storyIds = Object.keys(contentStore.content.categories[route.params.categoryId].stories);

    const index = storyIds.indexOf(currentId);
    const nextIndex = index + 1 >= storyIds.length ? 0 : index + 1;

    return storyIds[nextIndex];
})

function onSwiper(_swiper){
    swiper.value = _swiper;
}

function onButtonClick(i){
    swiper.value?.slideTo(i);
}

function isButtonActive(i){
    return swiper.value?.realIndex === i;
}

function getTabComponent(tab){
    if(tab.type === 'quote'){
        return Quote;
    }

    if(tab.type === 'table'){
        return Table;
    }
}

</script>

<template>
    <div class="grow flex gap-x-9 pt-52 px-10 4k:px-16 4k:pt-72 pb-20">
        <div class="grow">
            <div ref="slider-container" class="rounded-lg bg-white/20 backdrop-blur-2xl h-full p-10 flex flex-col">
                <div class="flex gap-6 flex-wrap">
                    <button
                        v-for="(tab, i) in content.tabs"
                        :key="i"
                        type="button"
                        class="shrink-0 px-6 4k:px-10 pt-4 4k:pt-8 pb-3 4k:pb-6 r-text-22 font-medium rounded-md border border-white transition-colors duration-300 ease-in-out"
                        :class="isButtonActive(i) ? 'bg-white text-secondary' : 'text-white hover:bg-white hover:text-secondary'"
                        @click="onButtonClick(i)"
                        v-text="$localize(tab.title)"
                    >
                    </button>
                </div>
                <div class="grid h-full mt-11">
                    <Swiper
                        class="grow w-full overflow-hidden"
                        :slides-per-view="1"
                        :space-between="20"
                        @swiper="onSwiper"
                    >
                        <SwiperSlide
                            v-for="(tab, i) in content.tabs"
                            :key="i"
                        >
                            <div class="relative size-full overflow-y-auto no-scrollbar">
                                <component 
                                    :is="getTabComponent(tab)"
                                    :tab="tab"
                                    class="absolute left-0 top-0 size-full"
                                />
                            </div>
                        </SwiperSlide>
                    </Swiper>
                </div>
            </div>
        </div>
        <div class="shrink-0 w-72 flex flex-col">
            <div class="pb-10 mb-10 border-b-2 border-gray-600 space-y-6">
                <router-link
                    :to="{name: 'start'}"
                    v-text="$trans('result.home')"
                    class="block r-text-14 font-bold uppercase rounded-sm text-secondary border-2 border-secondary p-4 pb-3 text-center hover:bg-secondary hover:text-white transition-colors duration-200"
                ></router-link>
                <router-link
                    :to="{name: 'category', params: {categoryId: route.params.categoryId}}"
                    v-text="$trans('result.overview')"
                    class="block r-text-14 font-bold uppercase rounded-sm text-secondary border-2 border-secondary p-4 pb-3 text-center hover:bg-secondary hover:text-white transition-colors duration-200"
                ></router-link>
                <router-link
                    :to="{name: 'story', params: {categoryId: route.params.categoryId, storyId: nextStoryId}}"
                    v-text="$trans('result.next')"
                    class="block r-text-14 font-bold uppercase rounded-sm text-secondary border-2 border-secondary p-4 pb-3 text-center hover:bg-secondary hover:text-white transition-colors duration-200"
                ></router-link>
            </div>
            <div
                v-if="content.cta.types.includes('qr-code-link')"
                class="mb-6"
            >
                <h2
                    v-text="$localize(content.cta.text)"
                    class="text-secondary font-bold uppercase mb-5 r-text-14"
                    v-if="!content.cta.types.includes('regular-link')"
                >
                </h2>

                <div class="border-2 border-secondary rounded-sm aspect-square p-12">
                    <img class="w-full" :src="$localize(content.cta.qrCode)">
                </div>
            </div>
            <a
                v-if="content.cta.types.includes('regular-link')"
                :href="$localize(content.cta.href)"
                :target="$localize(content.cta.target)"
                class="group mt-auto text-secondary r-text-22 font-bold uppercase flex items-center gap-x-2"
            >
                <span v-text="$localize(content.cta.text)"></span>
                <Icon class="shrink-0 size-4 -mt-2 group-hover:translate-x-2 transition-transform" name="arrow-right-small" />
            </a>
        </div>
    </div>
</template>
